import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cloneDeep } from 'lodash'
import { EllipsisVertical, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotificationTemplateActions } from './useNotificationTemplateActions'

const menuActionLabel = (loading: boolean, label: string) => (
  <span className='flex w-full items-center gap-2'>
    {label}
    {loading ? (
      <Loader2 className='ml-auto h-4 w-4 shrink-0 animate-spin' />
    ) : null}
  </span>
)

type Props = {
  template: Partial<wowaudit.NotificationTemplate>
  setOpenTemplate: (template: Partial<wowaudit.NotificationTemplate>) => void
  setOpenWarningModal?: (templateId: number) => void
}

const TemplateContextMenu = ({
  template,
  setOpenTemplate,
  setOpenWarningModal,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const {
    triggerTestMessage,
    testMessageLoading,
    triggerDeleteTemplate,
    deleteTemplateLoading,
    setTemplateSuspended,
    suspendLoading,
  } = useNotificationTemplateActions(template, {
    onActionComplete: () => setOpen(false),
  })

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger>
        <div className='flex items-center justify-center cursor-pointer h-5'>
          <EllipsisVertical size={18} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        alignOffset={-10}
        className='w-42'
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuItem onClick={() => setOpenTemplate(cloneDeep(template))}>
          {t('notifications.edit-template')}
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled={!template.id || suspendLoading}
          onClick={(e) => {
            e.preventDefault()
            if (!template.id || suspendLoading) return

            void setTemplateSuspended(!template.suspended)
          }}
        >
          {menuActionLabel(
            suspendLoading,
            t(
              template.suspended
                ? 'notifications.activate-template'
                : 'notifications.suspend-template',
            ),
          )}
        </DropdownMenuItem>

        {setOpenWarningModal && template.id ? (
          <DropdownMenuItem
            onClick={() => setOpenWarningModal(template.id as number)}
          >
            {t('notifications.show-warnings')}
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuItem
          disabled={testMessageLoading}
          onClick={(e) => {
            e.preventDefault()
            if (testMessageLoading) return
            triggerTestMessage()
          }}
        >
          {menuActionLabel(
            testMessageLoading,
            t(
              testMessageLoading
                ? 'notifications.sending-test-message'
                : 'notifications.send-test-message',
            ),
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!template.id || deleteTemplateLoading}
          onClick={(e) => {
            e.preventDefault()
            triggerDeleteTemplate()
          }}
          className='text-destructive-foreground focus:text-destructive-foreground'
        >
          {t('notifications.delete-template')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TemplateContextMenu
